import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import Skeleton from '@mui/material/Skeleton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import StoreIcon from '@mui/icons-material/Store';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { getStoredUser } from '../../api/client';
import {
  archiveOtherDeduction,
  bulkUpdateOtherDeductionTypes,
  createOtherDeduction,
  createOtherDeductionType,
  deleteOtherDeduction,
  deleteOtherDeductionType,
  getOtherDeductions,
  getOtherDeductionTypes,
  unarchiveOtherDeduction,
  updateOtherDeduction
} from '../../api/otherDeductions';
import { listStores } from '../../api/stores';
import { getEmployeesByStoreFromTimekeep, getStoresFromTimekeep } from '../../api/timekeep';
import { exportToCSV } from '../../utils/exportUtils';
import {
  buildProcurementDeductionTitle,
  buildDepartmentTypeMapFromRecords,
  FINALIZED_DEDUCTION_STATUSES,
  getDefaultDepartmentTypeMap,
  getDeductionTypeOptionsForRecord,
  getEmptyCustomTypeMap,
  isDeductionDashboardOwner,
  isValidCustomDeductionTypeName,
  mergeDepartmentTypeMaps,
  mergeEmployeeLists,
  normalizeApiDeductionRecord,
  normalizeCustomTypesMeta,
  normalizeDepartmentTypeOptions,
  normalizeProcurementTypeSelection,
  splitProcurementDeductionTitle,
  normalizeStoreKey
} from '../../utils/other-deductions';
import {
  calculateDeductionLastDate,
  calculateRecurringInstallmentCount,
  buildInstallmentAmounts,
  buildEmployeeOptionsByStore,
  getNormalizedRecurringAmount,
  createEmptyForm,
  currencyFormatter,
  departmentConfigs,
  formatDeductionDate,
  formatDeductionRange,
  statusPalette
} from './Deductions/config';
import { getAccessibleDeductionDepartments } from './Deductions/access';
import DeductionsHeader from './Deductions/components/DeductionsHeader';
import DeductionsOverview from './Deductions/components/DeductionsOverview';
import DeductionsStoreTable from './Deductions/components/DeductionsStoreTable';
import DeductionsEmployeesTable from './Deductions/components/DeductionsEmployeesTable';

const PeopleIcon = PeopleAltOutlinedIcon;
const TimelineIcon = TimelineOutlinedIcon;
const DeductionDialog = lazy(() => import('./Deductions/components/DeductionDialog'));
const DeductionProgressDialog = lazy(() => import('./Deductions/components/DeductionProgressDialog'));
const ProcurementTypePricingDialog = lazy(() => import('./Deductions/components/ProcurementTypePricingDialog'));

const payrollStatusPalette = {
  deducted: { label: 'Deducted' },
  scheduled: { label: 'Scheduled' },
  pending: { label: 'Pending' },
  archived: { label: 'Archived' }
};

const sanitizeFileName = (value = 'deductions') =>
  String(value)
    .trim()
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_') || 'deductions';

const formatPdfAmount = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const formatFormCurrencyValue = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : '';
};

const areCurrencyValuesEqual = (leftValue, rightValue) => {
  const leftNumber = Number(leftValue);
  const rightNumber = Number(rightValue);

  if (!Number.isFinite(leftNumber) || !Number.isFinite(rightNumber)) {
    return String(leftValue || '') === String(rightValue || '');
  }

  return Number(leftNumber.toFixed(2)) === Number(rightNumber.toFixed(2));
};

const mergeSavedCustomTypes = (existingItems = [], savedItems = []) => {
  const nextItems = [...existingItems];

  savedItems.forEach((savedItem) => {
    const normalizedSavedItem = {
      id: savedItem.id || savedItem._id,
      name: savedItem.name,
      price: Number(savedItem.price || 0)
    };
    const existingIndex = nextItems.findIndex((item) => (
      item.id === normalizedSavedItem.id ||
      item.name.toLowerCase() === normalizedSavedItem.name.toLowerCase()
    ));

    if (existingIndex >= 0) {
      nextItems[existingIndex] = {
        ...nextItems[existingIndex],
        ...normalizedSavedItem
      };
      return;
    }

    nextItems.push(normalizedSavedItem);
  });

  return nextItems;
};

const buildStoreCode = (storeName = '') =>
  String(storeName)
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 4)
    .toUpperCase();

const buildCutoffRange = (dateValue, frequency) => {
  const payrollDate = dayjs(dateValue);
  if (!payrollDate.isValid()) {
    return '-';
  }

  if (frequency === 'Monthly') {
    return formatDeductionRange(payrollDate.startOf('month').format('YYYY-MM-DD'), payrollDate.endOf('month').format('YYYY-MM-DD'));
  }

  const cutoffStart = payrollDate.date() <= 15 ? payrollDate.startOf('month') : payrollDate.date(16);
  const cutoffEnd = payrollDate.date() <= 15 ? payrollDate.date(15) : payrollDate.endOf('month');
  return formatDeductionRange(cutoffStart.format('YYYY-MM-DD'), cutoffEnd.format('YYYY-MM-DD'));
};

const formatActualPayrollRange = (payrollEntry) => {
  if (!payrollEntry?.payrollFrom || !payrollEntry?.payrollTo) {
    return '';
  }

  return formatDeductionRange(payrollEntry.payrollFrom, payrollEntry.payrollTo);
};

const buildPayrollRecordLabel = (payrollEntry, fallbackValue) => {
  const payrollId = String(payrollEntry?.payrollId || '').trim();
  return payrollId || fallbackValue;
};

const resolvePayrollActor = (value) => {
  const normalizedValue = String(value || '').trim();
  return normalizedValue || '-';
};

const resolveStepDate = (row, stepIndex, totalSteps) => {
  const firstDate = dayjs(row.firstDeductionDate || row.lastDeductionDate);
  const lastDate = dayjs(row.lastDeductionDate || row.firstDeductionDate);

  if (!firstDate.isValid()) {
    return dayjs();
  }

  if (row.frequency === 'One time' || totalSteps <= 1 || !lastDate.isValid() || firstDate.isSame(lastDate, 'day')) {
    return firstDate;
  }

  const daySpan = Math.max(lastDate.diff(firstDate, 'day'), 0);
  const interpolatedOffset = Math.round((daySpan / Math.max(totalSteps - 1, 1)) * stepIndex);
  return firstDate.add(interpolatedOffset, 'day');
};

const buildPayrollLinkages = (row) => {
  if (!row?.hasDeduction) {
    return [];
  }

  const totalSteps = row.frequency === 'One time' ? 1 : Math.max(Number(row.totalInstallments) || 1, 1);
  const completedSteps =
    row.frequency === 'One time'
      ? row.status === 'completed'
        ? 1
        : 0
      : Math.min(Math.max(Number(row.paidInstallments) || 0, 0), totalSteps);
  const storeCode = buildStoreCode(row.store);
  const departmentCode = String(row.department || 'ded').slice(0, 3).toUpperCase();
  const appliedPayrolls = Array.isArray(row.appliedPayrolls)
    ? [...row.appliedPayrolls].sort((left, right) => Number(left.installmentNumber || 0) - Number(right.installmentNumber || 0))
    : [];
  const scheduledInstallmentAmounts = buildInstallmentAmounts({
    frequency: row.frequency,
    amount: row.amount,
    totalAmount: row.totalAmount,
    totalInstallments: totalSteps
  });

  return Array.from({ length: totalSteps }, (_unusedValue, index) => {
    const stepNumber = index + 1;
    const payrollDate = resolveStepDate(row, index, totalSteps);
    const appliedPayroll =
      row.frequency === 'One time'
        ? appliedPayrolls[0] || null
        : appliedPayrolls.find((item) => Number(item.installmentNumber || 0) === stepNumber) || null;

    let payrollStatus = 'scheduled';
    if (appliedPayroll || stepNumber <= completedSteps) {
      payrollStatus = 'deducted';
    } else if (row.status === 'archived') {
      payrollStatus = 'archived';
    } else if (row.status === 'pending') {
      payrollStatus = 'pending';
    }

    return {
      stepLabel:
        row.frequency === 'One time'
          ? 'One-time deduction'
          : `Installment ${stepNumber} of ${totalSteps}`,
      payrollRecordId:
        payrollStatus === 'deducted'
          ? buildPayrollRecordLabel(
              appliedPayroll,
              `${departmentCode}-${storeCode}-${String(stepNumber).padStart(2, '0')}`
            )
          : 'Pending',
      cutoffRange:
        payrollStatus === 'deducted'
          ? formatActualPayrollRange(appliedPayroll) || buildCutoffRange(payrollDate, row.frequency)
          : 'Pending',
      deductionDate: formatDeductionDate(appliedPayroll?.payrollFrom || payrollDate.format('YYYY-MM-DD')),
      amount: appliedPayroll
        ? Number(appliedPayroll.amount || 0)
        : Number(scheduledInstallmentAmounts[index] || 0),
      status: payrollStatus,
      generatedBy: payrollStatus === 'deducted' ? resolvePayrollActor(appliedPayroll?.generatedBy) : 'Pending',
      approvedBy: payrollStatus === 'deducted' ? resolvePayrollActor(appliedPayroll?.approvedBy) : 'Pending'
    };
  });
};

const getDeductionProgressValues = (row) => {
  const scheduledInstallmentAmounts = row?.hasDeduction
    ? buildInstallmentAmounts({
        frequency: row.frequency,
        amount: row.amount,
        totalAmount: row.totalAmount,
        totalInstallments: row.totalInstallments
      })
    : [];
  const targetAmount = row?.hasDeduction
    ? Number(
        row.totalAmount ||
        (row.frequency === 'One time'
          ? row.amount || 0
          : (Number(row.amount || 0) || 0) * Math.max(Number(row.totalInstallments) || 1, 1))
      )
    : 0;
  const deductedAmountFromLinks = row?.hasDeduction && Array.isArray(row.appliedPayrolls)
    ? row.appliedPayrolls.reduce((sum, item) => sum + (Number(item.amount || 0) || 0), 0)
    : 0;
  const deductedAmountFromInstallments = row?.hasDeduction
    ? scheduledInstallmentAmounts
        .slice(0, Math.min(Math.max(Number(row.paidInstallments) || 0, 0), scheduledInstallmentAmounts.length))
        .reduce((sum, value) => sum + (Number(value || 0) || 0), 0)
    : 0;
  const deductedAmount = Math.min(targetAmount, Math.max(deductedAmountFromLinks, deductedAmountFromInstallments));
  const remainingAmount = Math.max(targetAmount - deductedAmount, 0);
  const progressPercentage = targetAmount > 0
    ? Math.min(100, Math.round((deductedAmount / targetAmount) * 100))
    : 0;

  return {
    deductedAmount,
    targetAmount,
    remainingAmount,
    progressPercentage
  };
};

const exportEmployeeDeductionsPdf = ({ employeeRow, deductionRows, activeDepartment }) => {
  const rows = deductionRows.filter(Boolean).map((row) => ({
    ...row,
    hasDeduction: true
  }));
  if (!employeeRow || rows.length === 0) {
    return;
  }

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4'
  });
  const pageWidth = doc.internal.pageSize.getWidth();
  const generatedAt = dayjs().format('MMM DD, YYYY hh:mm A');
  const accent = activeDepartment?.accent || '#0891b2';

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(17, 24, 39);
  doc.text('Employee Deductions', 40, 42);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  doc.text(`Generated: ${generatedAt}`, pageWidth - 40, 42, { align: 'right' });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(17, 24, 39);
  doc.text(employeeRow.employeeName || '-', 40, 68);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(107, 114, 128);
  doc.text(`${employeeRow.employeeId || '-'} - ${employeeRow.store || '-'}`, 40, 84);

  rows.forEach((row) => {
    const progress = getDeductionProgressValues(row);
    const titleY = (doc.lastAutoTable?.finalY || 82) + 24;

    if (titleY > 500) {
      doc.addPage();
    }

    const currentY = titleY > 500 ? 42 : titleY;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(17, 24, 39);
    doc.text(row.deductionType || 'Deduction', 40, currentY);

    doc.autoTable({
      startY: currentY + 10,
      head: [['Frequency', 'Window', 'Status', 'Progress', 'Amount', 'Remaining']],
      body: [[
        row.frequency || '-',
        formatDeductionRange(row.firstDeductionDate, row.lastDeductionDate),
        statusPalette[row.status]?.label || row.status || '-',
        `${progress.progressPercentage}%`,
        `${formatPdfAmount(progress.deductedAmount)} / ${formatPdfAmount(progress.targetAmount)}`,
        formatPdfAmount(progress.remainingAmount)
      ]],
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 8,
        cellPadding: 5,
        lineColor: [229, 231, 235],
        lineWidth: 0.4,
        textColor: [31, 41, 55],
        overflow: 'linebreak'
      },
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: [55, 65, 81],
        fontStyle: 'bold'
      },
      columnStyles: {
        4: { halign: 'right' },
        5: { halign: 'right' }
      },
      margin: { left: 40, right: 40 }
    });

    doc.autoTable({
      startY: (doc.lastAutoTable?.finalY || currentY + 10) + 8,
      head: [[
        'Step',
        'Payroll Record',
        'Cutoff Range',
        'Deduction Date',
        'Amount',
        'Status',
        'Generated By',
        'Approved By'
      ]],
      body: buildPayrollLinkages(row).map((item) => [
        item.stepLabel,
        item.payrollRecordId,
        item.cutoffRange,
        item.deductionDate,
        formatPdfAmount(item.amount),
        payrollStatusPalette[item.status]?.label || item.status,
        item.generatedBy,
        item.approvedBy
      ]),
      theme: 'striped',
      styles: {
        font: 'helvetica',
        fontSize: 7.2,
        cellPadding: 5,
        overflow: 'linebreak',
        textColor: [31, 41, 55],
        lineColor: [229, 231, 235],
        lineWidth: 0.3
      },
      headStyles: {
        fillColor: accent,
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [248, 250, 252]
      },
      columnStyles: {
        0: { cellWidth: 112, fontStyle: 'bold' },
        1: { cellWidth: 110 },
        2: { cellWidth: 122 },
        3: { cellWidth: 86 },
        4: { cellWidth: 76, halign: 'right' },
        5: { cellWidth: 66 },
        6: { cellWidth: 98 },
        7: { cellWidth: 98 }
      },
      margin: { left: 40, right: 40 },
      didDrawPage: () => {
        const pageNumber = doc.internal.getNumberOfPages();
        const pageHeight = doc.internal.pageSize.getHeight();
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(107, 114, 128);
        doc.text(`Page ${pageNumber}`, pageWidth - 40, pageHeight - 24, { align: 'right' });
      }
    });
  });

  doc.save(`${sanitizeFileName(`${employeeRow.employeeName || employeeRow.employeeId || 'employee'}_${employeeRow.store || 'store'}_deductions`)}.pdf`);
};

const Deductions = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { department = 'fgi' } = useParams();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const currentUser = getStoredUser();
  const isDashboardOwner = isDeductionDashboardOwner(currentUser);
  const accessibleDepartments = getAccessibleDeductionDepartments(currentUser);
  const fallbackDepartment = accessibleDepartments[0]?.key || 'fgi';
  const selectedStoreName = searchParams.get('name') || '';
  const isStoreDetailView = location.pathname.endsWith('/store');
  const visibleGroupAccess = Array.isArray(currentUser?.groupAccess) ? currentUser.groupAccess.filter(Boolean) : [];
  const visibleDirectStoreAccess = Array.isArray(currentUser?.storeAccess) ? currentUser.storeAccess.filter(Boolean) : [];

  const [records, setRecords] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeAccessSources, setStoreAccessSources] = useState({});
  const [storesLoading, setStoresLoading] = useState(true);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [storeEmployees, setStoreEmployees] = useState({});
  const [storeEmployeeLoadState, setStoreEmployeeLoadState] = useState({});
  const [departmentTypeOptions, setDepartmentTypeOptions] = useState(getDefaultDepartmentTypeMap);
  const [customDeductionTypesByDepartment, setCustomDeductionTypesByDepartment] = useState(getEmptyCustomTypeMap);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [progressDialogRow, setProgressDialogRow] = useState(null);
  const [procurementPricingDialogOpen, setProcurementPricingDialogOpen] = useState(false);
  const [procurementTypeRows, setProcurementTypeRows] = useState([]);
  const [procurementTypeLoading, setProcurementTypeLoading] = useState(false);
  const [procurementTypeSaving, setProcurementTypeSaving] = useState(false);
  const [procurementTypeSearchInput, setProcurementTypeSearchInput] = useState('');
  const [procurementTypeSearch, setProcurementTypeSearch] = useState('');
  const [procurementTypePage, setProcurementTypePage] = useState(1);
  const [procurementTypeRowsPerPage, setProcurementTypeRowsPerPage] = useState(10);
  const [procurementTypeTotalCount, setProcurementTypeTotalCount] = useState(0);
  const [procurementTypeDrafts, setProcurementTypeDrafts] = useState({});
  const [form, setForm] = useState(createEmptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recordsCacheRef = useRef({});
  const derivedTotalInstallments = useMemo(
    () => calculateRecurringInstallmentCount(form.totalAmount, form.amount, form.frequency),
    [form.amount, form.frequency, form.totalAmount]
  );
  const derivedLastDeductionDate = useMemo(
    () => (
      form.frequency === 'One time'
        ? form.firstDeductionDate || ''
        : calculateDeductionLastDate(form.firstDeductionDate, form.frequency, derivedTotalInstallments)
    ),
    [derivedTotalInstallments, form.firstDeductionDate, form.frequency]
  );
  const derivedRecurringAmount = useMemo(
    () => getNormalizedRecurringAmount(form.totalAmount, form.amount),
    [form.amount, form.totalAmount]
  );
  const procurementPriceLookup = useMemo(
    () => new Map(
      (customDeductionTypesByDepartment.procurement || []).map((item) => [item.id, Number(item.price || 0)])
    ),
    [customDeductionTypesByDepartment]
  );
  const procurementTypePendingEntries = useMemo(() => {
    return Object.entries(procurementTypeDrafts).filter(([typeId, priceValue]) => {
      const numericPrice = Number(priceValue);

      if (!Number.isFinite(numericPrice) || numericPrice < 0) {
        return false;
      }

      return Number(numericPrice.toFixed(2)) !== Number((procurementPriceLookup.get(typeId) || 0).toFixed(2));
    });
  }, [procurementPriceLookup, procurementTypeDrafts]);
  const syncCustomTypesForDepartment = useCallback((departmentKey, savedTypes = []) => {
    if (!Array.isArray(savedTypes) || savedTypes.length === 0) {
      return;
    }

    const normalizedSavedTypes = savedTypes.map((item) => ({
      id: item.id || item._id,
      name: item.name,
      price: Number(item.price || 0)
    }));

    setCustomDeductionTypesByDepartment((currentState) => {
      const nextItems = mergeSavedCustomTypes(currentState[departmentKey] || [], normalizedSavedTypes);
      const baseTypes = recordsCacheRef.current[departmentKey]?.typesByDepartment?.[departmentKey]
        || departmentConfigs[departmentKey]?.types
        || [];

      recordsCacheRef.current[departmentKey] = {
        ...(recordsCacheRef.current[departmentKey] || {}),
        records: recordsCacheRef.current[departmentKey]?.records || records,
        typesByDepartment: {
          [departmentKey]: normalizeDepartmentTypeOptions(departmentKey, [
            ...baseTypes,
            ...normalizedSavedTypes.map((item) => item.name)
          ])
        },
        customTypesByDepartment: {
          [departmentKey]: nextItems
        }
      };

      return {
        ...currentState,
        [departmentKey]: nextItems
      };
    });

    setDepartmentTypeOptions((currentOptions) => ({
      ...currentOptions,
      [departmentKey]: normalizeDepartmentTypeOptions(departmentKey, [
        ...(currentOptions[departmentKey] || departmentConfigs[departmentKey]?.types || []),
        ...normalizedSavedTypes.map((item) => item.name)
      ])
    }));
  }, [records]);

  useEffect(() => {
    if (accessibleDepartments.length === 0) {
      navigate('/', { replace: true });
      return;
    }

    const canAccessCurrentDepartment = accessibleDepartments.some((item) => item.key === department);

    if (!departmentConfigs[department] || !canAccessCurrentDepartment) {
      navigate(`/deductions/${fallbackDepartment}`, { replace: true });
    }
  }, [accessibleDepartments, department, fallbackDepartment, navigate]);

  useEffect(() => {
    let isMounted = true;

    const loadStores = async () => {
      setStoresLoading(true);
      const user = getStoredUser();
      const isOwner = isDeductionDashboardOwner(user);
      const mergedStores = new Set();
      const nextStoreAccessSources = {};
      const userGroups = Array.isArray(user?.groupAccess) ? user.groupAccess.filter(Boolean) : [];
      const userStores = Array.isArray(user?.storeAccess) ? user.storeAccess.filter(Boolean) : [];

      const attachSource = (storeName, source) => {
        const normalizedStore = normalizeStoreKey(storeName);
        if (!normalizedStore) return;

        if (!nextStoreAccessSources[normalizedStore]) {
          nextStoreAccessSources[normalizedStore] = [];
        }

        const alreadyExists = nextStoreAccessSources[normalizedStore].some(
          (item) => item.type === source.type && item.label === source.label
        );

        if (!alreadyExists) {
          nextStoreAccessSources[normalizedStore].push(source);
        }
      };

      try {
        if (isOwner || userGroups.length > 0) {
          const timekeepResponse = await getStoresFromTimekeep(userGroups);
          if (timekeepResponse?.success) {
            timekeepResponse.data.forEach((store) => {
              if (store) {
                mergedStores.add(String(store).trim());
              }
            });
          }
        }

        if (!isOwner && userGroups.length > 0) {
          const groupStoreResponses = await Promise.all(
            userGroups.map(async (groupName) => {
              try {
                const response = await getStoresFromTimekeep([groupName]);
                return {
                  groupName,
                  stores: response?.success ? response.data : []
                };
              } catch (error) {
                console.error(`Failed to resolve stores for group ${groupName}:`, error);
                return {
                  groupName,
                  stores: []
                };
              }
            })
          );

          groupStoreResponses.forEach(({ groupName, stores: groupStores }) => {
            groupStores.forEach((storeName) => {
              attachSource(storeName, { type: 'group', label: groupName });
            });
          });
        }
      } catch (error) {
        console.error('Failed to load stores from timekeep:', error);
      }

      try {
        if (isOwner && !mergedStores.size) {
          const storesResponse = await listStores();
          (storesResponse?.data?.stores || []).forEach((storeObj) => {
            if (storeObj?.store) {
              mergedStores.add(String(storeObj.store).trim());
            }
          });
        }
      } catch (error) {
        console.error('Failed to load stores from payroll API:', error);
      }

      userStores.forEach((store) => {
        if (store) {
          mergedStores.add(String(store).trim());
          attachSource(store, { type: 'store', label: String(store).trim() });
        }
      });

      if (isMounted) {
        setStores([...mergedStores].sort((left, right) => left.localeCompare(right)));
        setStoreAccessSources(nextStoreAccessSources);
        setStoresLoading(false);
      }
    };

    loadStores();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!records.length) {
      return;
    }

    setStores((currentStores) =>
      [...new Set([...currentStores, ...records.map((record) => record.store).filter(Boolean)])].sort((left, right) =>
        left.localeCompare(right)
      )
    );
  }, [records]);

  const loadEmployeesForStore = useCallback(
    async (storeName) => {
      if (!storeName) {
        return;
      }

      const loadState = storeEmployeeLoadState[storeName];
      if (loadState) {
        return;
      }

      setStoreEmployeeLoadState((currentState) => ({
        ...currentState,
        [storeName]: 'loading'
      }));

      try {
        const response = await getEmployeesByStoreFromTimekeep(storeName);
        const normalizedEmployees = mergeEmployeeLists(response?.data || []);

        setStoreEmployees((currentDirectory) => ({
          ...currentDirectory,
          [storeName]: normalizedEmployees
        }));
        setStoreEmployeeLoadState((currentState) => ({
          ...currentState,
          [storeName]: 'loaded'
        }));
      } catch (error) {
        console.error(`Failed to load employees for ${storeName}:`, error);
        setStoreEmployeeLoadState((currentState) => ({
          ...currentState,
          [storeName]: 'error'
        }));
      }
    },
    [storeEmployeeLoadState]
  );

  const loadDeductions = useCallback(async (options = {}) => {
    const { force = false, signal } = options;
    const cacheKey = department;

    if (!force && recordsCacheRef.current[cacheKey]) {
      const cachedEntry = recordsCacheRef.current[cacheKey];
      setRecords(cachedEntry.records);
      setCustomDeductionTypesByDepartment((currentState) => ({
        ...currentState,
        ...cachedEntry.customTypesByDepartment
      }));
      setDepartmentTypeOptions((currentOptions) =>
        mergeDepartmentTypeMaps(currentOptions, cachedEntry.typesByDepartment)
      );
      setRecordsLoading(false);
      return;
    }

    setRecordsLoading(true);

    try {
      const response = await getOtherDeductions(
        { department, include_archived: true },
        { signal }
      );
      const nextRecords = Array.isArray(response?.data)
        ? response.data.map(normalizeApiDeductionRecord)
        : [];
      const typesByDepartment = response?.meta?.types_by_department || {};
      const customTypesByDepartment = normalizeCustomTypesMeta(
        response?.meta?.custom_types_by_department || {}
      );

      setRecords(nextRecords);
      setCustomDeductionTypesByDepartment((currentState) => ({
        ...currentState,
        ...customTypesByDepartment
      }));
      recordsCacheRef.current[cacheKey] = {
        records: nextRecords,
        typesByDepartment,
        customTypesByDepartment
      };
      setDepartmentTypeOptions((currentOptions) =>
        mergeDepartmentTypeMaps(currentOptions, typesByDepartment)
      );
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Failed to load other deductions:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to load deductions dashboard data.',
        { variant: 'error' }
      );
    } finally {
      setRecordsLoading(false);
    }
  }, [department, enqueueSnackbar]);

  useEffect(() => {
    const controller = new AbortController();
    loadDeductions({ signal: controller.signal });

    return () => {
      controller.abort();
    };
  }, [loadDeductions]);

  useEffect(() => {
    if (!procurementPricingDialogOpen) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setProcurementTypeSearch(procurementTypeSearchInput.trim());
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [procurementPricingDialogOpen, procurementTypeSearchInput]);

  const loadProcurementTypes = useCallback(async () => {
    if (!procurementPricingDialogOpen || department !== 'procurement') {
      return;
    }

    try {
      setProcurementTypeLoading(true);
      const response = await getOtherDeductionTypes({
        department: 'procurement',
        page: procurementTypePage,
        limit: procurementTypeRowsPerPage,
        search: procurementTypeSearch || undefined
      });
      const nextRows = Array.isArray(response?.data)
        ? response.data.map((item) => ({
            id: item.id || item._id,
            name: item.name,
            price: Number(item.price || 0)
          }))
        : [];

      setProcurementTypeRows(nextRows);
      setProcurementTypeTotalCount(Number(response?.meta?.total || 0));
      syncCustomTypesForDepartment('procurement', nextRows);
    } catch (error) {
      console.error('Failed to load procurement deduction item prices:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to load procurement deduction item prices.',
        { variant: 'error' }
      );
    } finally {
      setProcurementTypeLoading(false);
    }
  }, [
    department,
    enqueueSnackbar,
    procurementPricingDialogOpen,
    procurementTypePage,
    procurementTypeRowsPerPage,
    procurementTypeSearch,
    syncCustomTypesForDepartment
  ]);

  useEffect(() => {
    loadProcurementTypes();
  }, [loadProcurementTypes]);

  useEffect(() => {
    if (department !== 'procurement' && procurementPricingDialogOpen) {
      setProcurementPricingDialogOpen(false);
    }
  }, [department, procurementPricingDialogOpen]);

  const activeDepartment = {
    ...(departmentConfigs[department] || departmentConfigs.fgi),
    key: departmentConfigs[department] ? department : 'fgi'
  };
  const activeDepartmentTypes = Array.isArray(departmentTypeOptions[department])
    ? departmentTypeOptions[department]
    : (activeDepartment.types || []);
  const departmentRows = useMemo(
    () => records.filter((record) => record.department === department),
    [department, records]
  );
  const fallbackEmployeeDirectory = useMemo(() => buildEmployeeOptionsByStore(stores, records), [stores, records]);
  const employeeDirectory = useMemo(() => {
    const allStoreNames = new Set([
      ...stores,
      ...Object.keys(fallbackEmployeeDirectory),
      ...Object.keys(storeEmployees)
    ]);
    const nextDirectory = {};

    allStoreNames.forEach((storeName) => {
      nextDirectory[storeName] = mergeEmployeeLists(
        storeEmployees[storeName] || [],
        fallbackEmployeeDirectory[storeName] || []
      );
    });

    return nextDirectory;
  }, [fallbackEmployeeDirectory, storeEmployees, stores]);

  useEffect(() => {
    if (form.store) {
      loadEmployeesForStore(form.store);
    }
  }, [form.store, loadEmployeesForStore]);

  useEffect(() => {
    if (isStoreDetailView && selectedStoreName) {
      loadEmployeesForStore(selectedStoreName);
    }
  }, [isStoreDetailView, loadEmployeesForStore, selectedStoreName]);

  const allStoreRows = useMemo(() => {
    return stores
      .map((store) => {
        const storeRecords = departmentRows.filter((record) => record.store === store);
        if (storeRecords.length === 0) {
          return null;
        }

        const currentStoreRecords = storeRecords.filter(
          (record) => !FINALIZED_DEDUCTION_STATUSES.includes(record.status)
        );
        const employeeCountWithDeductions = new Set(currentStoreRecords.map((record) => record.employeeId)).size;
        const totalEmployees = employeeDirectory[store]?.length || new Set(storeRecords.map((record) => record.employeeId)).size;
        const firstDates = storeRecords.map((record) => record.firstDeductionDate).filter(Boolean).sort();
        const lastDates = storeRecords.map((record) => record.lastDeductionDate).filter(Boolean).sort();

        return {
          store,
          totalEmployees,
          employeesWithDeductions: employeeCountWithDeductions,
          employeesWithoutDeductions: Math.max(totalEmployees - employeeCountWithDeductions, 0),
          activeCount: currentStoreRecords.filter((record) => record.status === 'active').length,
          monthlyExposure: currentStoreRecords
            .reduce((total, record) => total + Number(record.amount || 0), 0),
          deductionWindow: formatDeductionRange(firstDates[0], lastDates[lastDates.length - 1] || firstDates[0]),
          accessSource: (() => {
            if (isDashboardOwner) {
              return {
                summary: 'Owner override',
                details: ['Visible via owner override']
              };
            }

            const normalizedStore = normalizeStoreKey(store);
            const sources = storeAccessSources[normalizedStore] || [];
            const storeSources = sources
              .filter((source) => source.type === 'store')
              .map((source) => source.label);
            const groupSources = sources
              .filter((source) => source.type === 'group')
              .map((source) => source.label);
            const details = [...storeSources, ...groupSources];

            if (details.length > 0) {
              const hasStoreSource = storeSources.length > 0;
              const hasGroupSource = groupSources.length > 0;
              const detailGroups = [];

              if (groupSources.length > 0) {
                detailGroups.push(`Group: ${groupSources.join(', ')}`);
              }

              if (storeSources.length > 0) {
                detailGroups.push(`Store: ${storeSources.join(', ')}`);
              }

              return {
                summary: hasStoreSource && hasGroupSource
                  ? 'Visible via group and store'
                  : hasStoreSource
                    ? 'Visible via store'
                    : 'Visible via group',
                details: detailGroups
              };
            }

            return null;
          })()
        };
      })
      .filter(Boolean);
  }, [departmentRows, employeeDirectory, stores, isDashboardOwner, storeAccessSources]);

  const storeRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return allStoreRows;
    }

    return allStoreRows.filter((storeRow) => storeRow.store.toLowerCase().includes(normalizedSearch));
  }, [allStoreRows, searchTerm]);
  const selectedStoreRow = useMemo(
    () => allStoreRows.find((storeRow) => storeRow.store === selectedStoreName) || null,
    [allStoreRows, selectedStoreName]
  );

  const selectedStoreEmployees = useMemo(() => {
    return employeeDirectory[selectedStoreName] || [];
  }, [employeeDirectory, selectedStoreName]);

  const selectedStoreRecords = useMemo(() => {
    return departmentRows.filter((record) => record.store === selectedStoreName);
  }, [departmentRows, selectedStoreName]);
  const selectedStoreCurrentRecords = useMemo(
    () => selectedStoreRecords.filter((record) => !FINALIZED_DEDUCTION_STATUSES.includes(record.status)),
    [selectedStoreRecords]
  );
  const selectedStoreHistoricalRecords = useMemo(
    () => selectedStoreRecords.filter((record) => FINALIZED_DEDUCTION_STATUSES.includes(record.status)),
    [selectedStoreRecords]
  );

  const employeeRows = useMemo(() => {
    if (!isStoreDetailView || !selectedStoreName) {
      return [];
    }

    const sourceEmployees = mergeEmployeeLists(
      selectedStoreEmployees,
      selectedStoreRecords.map((record) => ({
        employeeName: record.employeeName,
        employeeId: record.employeeId
      }))
    );
    const employeesWithCurrentDeductions = new Set(
      selectedStoreCurrentRecords.map((record) => record.employeeId)
    );
    const employeesWithHistoricalDeductions = new Set(
      selectedStoreHistoricalRecords.map((record) => record.employeeId)
    );

    const currentRows = [...selectedStoreCurrentRecords]
      .sort((left, right) => {
        const nameSort = left.employeeName.localeCompare(right.employeeName);
        if (nameSort !== 0) {
          return nameSort;
        }

        return String(left.firstDeductionDate || '').localeCompare(String(right.firstDeductionDate || ''));
      })
      .map((record) => ({
        ...record,
        hasDeduction: true,
        section: 'current'
      }));

    const availableRows = sourceEmployees
      .filter((employee) => !employeesWithCurrentDeductions.has(employee.employeeId))
      .sort((left, right) => {
        const leftHasHistory = employeesWithHistoricalDeductions.has(left.employeeId);
        const rightHasHistory = employeesWithHistoricalDeductions.has(right.employeeId);

        if (leftHasHistory !== rightHasHistory) {
          return leftHasHistory ? -1 : 1;
        }

        return left.employeeName.localeCompare(right.employeeName);
      })
      .map((employee) => ({
        ...createEmptyForm(),
        department,
        store: selectedStoreName,
        employeeName: employee.employeeName,
        employeeId: employee.employeeId,
        hasDeduction: false,
        status: 'none',
        section: 'available'
      }));

    const historyRows = [...selectedStoreHistoricalRecords]
      .sort((left, right) => {
        const lastDateSort = String(right.lastDeductionDate || '').localeCompare(String(left.lastDeductionDate || ''));
        if (lastDateSort !== 0) {
          return lastDateSort;
        }

        return left.employeeName.localeCompare(right.employeeName);
      })
      .map((record) => ({
        ...record,
        hasDeduction: true,
        section: 'history'
      }));

    const rows = [...currentRows, ...availableRows, ...historyRows];

    const normalizedSearch = searchTerm.trim().toLowerCase();
    return normalizedSearch
      ? rows.filter((row) => {
          return (
            row.employeeName.toLowerCase().includes(normalizedSearch) ||
            row.employeeId.toLowerCase().includes(normalizedSearch) ||
            row.deductionType.toLowerCase().includes(normalizedSearch) ||
            row.section.toLowerCase().includes(normalizedSearch)
          );
        })
      : rows;
  }, [
    department,
    isStoreDetailView,
    searchTerm,
    selectedStoreCurrentRecords,
    selectedStoreEmployees,
    selectedStoreHistoricalRecords,
    selectedStoreName,
    selectedStoreRecords
  ]);

  const storeRowsPageCount = Math.max(1, Math.ceil(storeRows.length / rowsPerPage));
  const employeeCurrentAndAvailableRows = useMemo(
    () => employeeRows.filter((row) => row.section !== 'history'),
    [employeeRows]
  );
  const employeeHistoryRows = useMemo(
    () => employeeRows.filter((row) => row.section === 'history'),
    [employeeRows]
  );
  const employeeRowsPageCount = Math.max(1, Math.ceil(employeeCurrentAndAvailableRows.length / rowsPerPage));
  const paginatedStoreRows = storeRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const paginatedEmployeeRows = useMemo(() => {
    const currentAndAvailableSlice = employeeCurrentAndAvailableRows.slice(
      (page - 1) * rowsPerPage,
      page * rowsPerPage
    );
    const shouldAppendHistory = page === 1 || employeeCurrentAndAvailableRows.length === 0;

    return shouldAppendHistory
      ? [...currentAndAvailableSlice, ...employeeHistoryRows]
      : currentAndAvailableSlice;
  }, [employeeCurrentAndAvailableRows, employeeHistoryRows, page, rowsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [department, isStoreDetailView, rowsPerPage, searchTerm, selectedStoreName, records]);

  useEffect(() => {
    if (!storesLoading && !recordsLoading && isStoreDetailView && !selectedStoreName) {
      navigate(`/deductions/${department}`, { replace: true });
    }
  }, [department, isStoreDetailView, navigate, recordsLoading, selectedStoreName, storesLoading]);

  useEffect(() => {
    if (!storesLoading && !recordsLoading && isStoreDetailView && selectedStoreName && !selectedStoreRow) {
      navigate(`/deductions/${department}`, { replace: true });
    }
  }, [department, isStoreDetailView, navigate, recordsLoading, selectedStoreName, selectedStoreRow, storesLoading]);

  const storeOptions = stores.map((store) => ({
    value: store,
    label: store
  }));
  const employeeOptions = (employeeDirectory[form.store] || []).map((employee) => ({
    value: employee.employeeId,
    label: employee.employeeName,
    description: employee.employeeId
  }));
  const deductionTypeOptions = useMemo(() => {
    const customTypes = customDeductionTypesByDepartment[department] || [];
    const options = activeDepartmentTypes.map((type) => {
      const matchingCustomType = customTypes.find(
        (item) => item.name.toLowerCase() === String(type).trim().toLowerCase()
      );

      return {
        value: type,
        label: type,
        description: matchingCustomType
          ? (
            department === 'procurement'
              ? `Saved price: ${currencyFormatter.format(Number(matchingCustomType.price || 0))}`
              : 'Saved option'
          )
          : '',
        isCustom: Boolean(matchingCustomType),
        typeId: matchingCustomType?.id,
        price: Number(matchingCustomType?.price || 0)
      };
    });

    if (department === 'procurement') {
      const selectedProcurementTypes = normalizeProcurementTypeSelection(form.procurementDeductionTypes || []);
      const detachedOptions = selectedProcurementTypes
        .filter((selectedType) => !options.some((option) => option.value === selectedType))
        .map((selectedType) => ({
          value: selectedType,
          label: selectedType,
          description: 'Existing saved value',
          isDetached: true,
          price: 0
        }));

      return [...detachedOptions, ...options];
    }

    if (form.deductionType && !options.some((option) => option.value === form.deductionType)) {
      return [
        {
          value: form.deductionType,
          label: form.deductionType,
          description: 'Existing saved value',
          isDetached: true
        },
        ...options
      ];
    }

    return options;
  }, [activeDepartmentTypes, customDeductionTypesByDepartment, department, form.deductionType, form.procurementDeductionTypes]);
  const procurementTypePriceByName = useMemo(() => new Map(
    deductionTypeOptions.map((option) => [String(option.value || '').trim().toLowerCase(), Number(option.price || 0)])
  ), [deductionTypeOptions]);
  const derivedProcurementOriginalTotal = useMemo(() => {
    if (department !== 'procurement') {
      return 0;
    }

    return normalizeProcurementTypeSelection(form.procurementDeductionTypes || []).reduce((sum, typeName) => (
      sum + Number(procurementTypePriceByName.get(typeName.toLowerCase()) || 0)
    ), 0);
  }, [department, form.procurementDeductionTypes, procurementTypePriceByName]);
  const totalAmountHelperText = useMemo(() => {
    const installmentHelper = !isNaN(derivedTotalInstallments) && derivedTotalInstallments > 0 && form.frequency !== 'One time'
      ? `${derivedTotalInstallments} installments will be generated.`
      : '';

    if (department !== 'procurement' || normalizeProcurementTypeSelection(form.procurementDeductionTypes || []).length === 0) {
      return installmentHelper;
    }

    const originalTotalLabel = `Original Total: ${currencyFormatter.format(derivedProcurementOriginalTotal)}`;
    if (form.procurementTotalOverridden) {
      return installmentHelper ? `${originalTotalLabel} ${installmentHelper}` : originalTotalLabel;
    }

    return installmentHelper || 'Auto-filled from selected procurement deduction items.';
  }, [
    department,
    derivedProcurementOriginalTotal,
    derivedTotalInstallments,
    form.frequency,
    form.procurementDeductionTypes,
    form.procurementTotalOverridden
  ]);
  const employeeOptionsLoadState = form.store ? storeEmployeeLoadState[form.store] : '';
  const employeeOptionsLoading = employeeOptionsLoadState === 'loading';
  const employeeOptionsHelperText = !form.store
    ? 'Select a store first so the employee list can be filtered.'
    : employeeOptionsLoading
      ? 'Loading employees for this store...'
      : employeeOptionsLoadState === 'error'
        ? 'Could not load store employees. You can change the store or try again.'
        : '';

  const departmentSummaryCards = [
    {
      title: 'Stores with deductions',
      value: allStoreRows.length,
      helper: 'Stores ready for drill-down management',
      icon: StoreIcon
    },
    {
      title: 'Employees with deductions',
      value: new Set(
        departmentRows
          .filter((record) => !FINALIZED_DEDUCTION_STATUSES.includes(record.status))
          .map((record) => record.employeeId)
      ).size,
      helper: 'Employees currently assigned a deduction',
      icon: PeopleIcon
    },
    {
      title: 'Active deductions',
      value: departmentRows.filter((record) => record.status === 'active').length,
      helper: 'Scheduled for the next payroll runs',
      icon: TimelineIcon
    },
    {
      title: 'Recurring exposure',
      value: currencyFormatter.format(
        departmentRows
          .filter((record) => !FINALIZED_DEDUCTION_STATUSES.includes(record.status))
          .reduce((total, record) => total + Number(record.amount || 0), 0)
      ),
      helper: `${departmentRows.length} deduction rows in this department`,
      icon: activeDepartment.icon
    }
  ];

  const detailSummaryCards = [
    {
      title: 'With deductions',
      value: new Set(selectedStoreCurrentRecords.map((record) => record.employeeId)).size,
      icon: PeopleIcon
    },
    {
      title: 'Without deductions',
      value: employeeRows.filter((row) => row.section === 'available').length,
      icon: StoreIcon
    },
    {
      title: 'Active deductions',
      value: selectedStoreCurrentRecords.filter((record) => record.status === 'active').length,
      icon: TimelineIcon
    },
    {
      title: 'Recurring exposure',
      value: currencyFormatter.format(
        selectedStoreCurrentRecords
          .reduce((total, record) => total + Number(record.amount || 0), 0)
      ),
      icon: activeDepartment.icon
    }
  ];

  const storeDeductionWindow = useMemo(() => {
    if (!selectedStoreRecords.length) {
      return 'No deduction dates yet';
    }

    const firstDates = selectedStoreRecords.map((record) => record.firstDeductionDate).filter(Boolean).sort();
    const lastDates = selectedStoreRecords.map((record) => record.lastDeductionDate).filter(Boolean).sort();
    return formatDeductionRange(firstDates[0], lastDates[lastDates.length - 1] || firstDates[0]);
  }, [selectedStoreRecords]);

  const syncRecord = (apiRecord) => {
    const normalizedRecord = normalizeApiDeductionRecord(apiRecord);

    setRecords((currentRecords) => {
      const existingIndex = currentRecords.findIndex((record) => record.id === normalizedRecord.id);
      const nextRecords = existingIndex >= 0
        ? currentRecords.map((record) => (record.id === normalizedRecord.id ? normalizedRecord : record))
        : [normalizedRecord, ...currentRecords];

      recordsCacheRef.current[normalizedRecord.department] = {
        records: nextRecords,
        typesByDepartment: buildDepartmentTypeMapFromRecords(nextRecords),
        customTypesByDepartment: {
          [normalizedRecord.department]: customDeductionTypesByDepartment[normalizedRecord.department] || []
        },
      };

      return nextRecords;
    });

    setDepartmentTypeOptions((currentOptions) =>
      mergeDepartmentTypeMaps(currentOptions, {
        [normalizedRecord.department]: getDeductionTypeOptionsForRecord(normalizedRecord)
      })
    );
  };

  const buildProcurementSelectionFormState = useCallback((currentForm, nextSelectionsInput) => {
    const nextSelections = normalizeProcurementTypeSelection(nextSelectionsInput);
    const nextOriginalTotal = nextSelections.reduce((sum, typeName) => (
      sum + Number(procurementTypePriceByName.get(typeName.toLowerCase()) || 0)
    ), 0);
    const nextOriginalTotalAmount = nextSelections.length > 0 ? formatFormCurrencyValue(nextOriginalTotal) : '';
    const shouldSyncTotalAmount =
      !currentForm.procurementTotalOverridden ||
      !String(currentForm.totalAmount || '').trim() ||
      areCurrencyValuesEqual(currentForm.totalAmount, currentForm.procurementOriginalTotalAmount);
    const nextTotalAmount = shouldSyncTotalAmount
      ? nextOriginalTotalAmount
      : currentForm.totalAmount;

    return {
      ...currentForm,
      deductionType: buildProcurementDeductionTitle(nextSelections),
      procurementDeductionTypes: nextSelections,
      procurementOriginalTotalAmount: nextOriginalTotalAmount,
      procurementTotalOverridden: shouldSyncTotalAmount
        ? false
        : !areCurrencyValuesEqual(nextTotalAmount, nextOriginalTotalAmount),
      totalAmount: nextTotalAmount,
      amount: currentForm.frequency === 'One time'
        ? nextTotalAmount
        : currentForm.amount
    };
  }, [procurementTypePriceByName]);

  useEffect(() => {
    if (department !== 'procurement' || !dialogOpen) {
      return;
    }

    const normalizedSelections = normalizeProcurementTypeSelection(form.procurementDeductionTypes || []);
    if (normalizedSelections.length === 0 || form.procurementTotalOverridden) {
      return;
    }

    const expectedTitle = buildProcurementDeductionTitle(normalizedSelections);
    const expectedOriginalTotalAmount = formatFormCurrencyValue(normalizedSelections.reduce((sum, typeName) => (
      sum + Number(procurementTypePriceByName.get(typeName.toLowerCase()) || 0)
    ), 0));

    if (
      form.deductionType === expectedTitle &&
      String(form.procurementOriginalTotalAmount || '') === String(expectedOriginalTotalAmount || '') &&
      areCurrencyValuesEqual(form.totalAmount, expectedOriginalTotalAmount)
    ) {
      return;
    }

    setForm((currentForm) => buildProcurementSelectionFormState(currentForm, normalizedSelections));
  }, [
    buildProcurementSelectionFormState,
    department,
    dialogOpen,
    form.deductionType,
    form.procurementDeductionTypes,
    form.procurementOriginalTotalAmount,
    form.procurementTotalOverridden,
    form.totalAmount,
    procurementTypePriceByName
  ]);

  const buildPayloadFromForm = () => {
    const isOneTime = form.frequency === 'One time';
    const deductionDate = form.firstDeductionDate;

    return {
      department,
      employee_id: form.employeeId,
      employee_name: form.employeeName,
      store: form.store,
      deduction_type: form.deductionType,
      deduction_type_items: department === 'procurement'
        ? normalizeProcurementTypeSelection(form.procurementDeductionTypes || [])
        : [],
      frequency: form.frequency,
      amount: isOneTime ? Number(form.totalAmount || form.amount || 0) : derivedRecurringAmount,
      total_amount: Number(form.totalAmount),
      paid_installments: isOneTime ? (form.status === 'completed' ? 1 : 0) : Number(form.paidInstallments),
      total_installments: isOneTime ? 1 : derivedTotalInstallments,
      first_deduction_date: deductionDate,
      last_deduction_date: isOneTime ? deductionDate : derivedLastDeductionDate,
      status: form.status,
      auto_stop: Boolean(form.autoStop),
      remarks: form.remarks || ''
    };
  };

  const handleRefresh = async () => {
    setSearchTerm('');
    setStoreEmployees({});
    setStoreEmployeeLoadState({});
    delete recordsCacheRef.current[department];
    await loadDeductions({ force: true });
    enqueueSnackbar('Deduction dashboard refreshed from saved data.', { variant: 'info' });
  };

  const handleExport = () => {
    if (isStoreDetailView) {
      const exportRows = employeeRows.map((row) => ({
        Employee: row.employeeName,
        'Employee ID': row.employeeId,
        Store: row.store,
        'Deduction Setup': row.hasDeduction ? 'Has deduction' : 'No deduction',
        [activeDepartment.typeLabel]: row.hasDeduction ? row.deductionType : '-',
        Frequency: row.hasDeduction ? row.frequency : '-',
        Amount: row.hasDeduction
          ? currencyFormatter.format(row.frequency === 'One time' ? row.totalAmount || row.amount : row.amount)
          : '-',
        Progress: row.hasDeduction
          ? row.frequency === 'One time'
            ? '-'
            : `${row.paidInstallments}/${row.totalInstallments}`
          : '-',
        'Deduction range': row.hasDeduction ? formatDeductionRange(row.firstDeductionDate, row.lastDeductionDate) : '-',
        Status: row.hasDeduction ? statusPalette[row.status]?.label || row.status : 'No deduction'
      }));

      exportToCSV(exportRows, `${department}-${selectedStoreName}-deductions`);
      return;
    }

    const exportRows = storeRows.map((row) => ({
      Department: activeDepartment.label,
      Store: row.store,
      'Total employees': row.totalEmployees,
      'With deductions': row.employeesWithDeductions,
      'Without deductions': row.employeesWithoutDeductions,
      'Active deductions': row.activeCount,
      'Deduction window': row.deductionWindow,
      Exposure: currencyFormatter.format(row.monthlyExposure)
    }));

    exportToCSV(exportRows, `${department}-deduction-stores`);
  };

  const handleCloseDialog = () => {
    if (isSubmitting) {
      return;
    }

    setDialogOpen(false);
    setFormErrors({});
  };

  const handleFormChange = (field, value) => {
    setForm((currentForm) => {
      if (field === 'procurementDeductionTypes' && department === 'procurement') {
        return buildProcurementSelectionFormState(currentForm, value);
      }

      if (field === 'frequency') {
        if (value === 'One time') {
          const deductionDate =
            currentForm.firstDeductionDate ||
            currentForm.lastDeductionDate ||
            createEmptyForm().firstDeductionDate;

          return {
            ...currentForm,
            frequency: value,
            amount: currentForm.totalAmount || currentForm.amount || '',
            paidInstallments: currentForm.status === 'completed' ? 1 : 0,
            totalInstallments: 1,
            firstDeductionDate: deductionDate,
            lastDeductionDate: deductionDate
          };
        }

        return {
          ...currentForm,
          frequency: value,
          amount: currentForm.amount || currentForm.totalAmount || '',
          paidInstallments: currentForm.frequency === 'One time' ? 0 : currentForm.paidInstallments
        };
      }

      if (field === 'firstDeductionDate' && currentForm.frequency === 'One time') {
        return {
          ...currentForm,
          firstDeductionDate: value,
          lastDeductionDate: value
        };
      }

      if (field === 'totalAmount' && currentForm.frequency === 'One time') {
        return {
          ...currentForm,
          totalAmount: value,
          amount: value,
          procurementTotalOverridden: department === 'procurement' && normalizeProcurementTypeSelection(currentForm.procurementDeductionTypes || []).length > 0
            ? !areCurrencyValuesEqual(value, currentForm.procurementOriginalTotalAmount)
            : currentForm.procurementTotalOverridden
        };
      }

      if (field === 'totalAmount') {
        return {
          ...currentForm,
          totalAmount: value,
          procurementTotalOverridden: department === 'procurement' && normalizeProcurementTypeSelection(currentForm.procurementDeductionTypes || []).length > 0
            ? !areCurrencyValuesEqual(value, currentForm.procurementOriginalTotalAmount)
            : currentForm.procurementTotalOverridden
        };
      }

      if (field === 'status' && currentForm.frequency === 'One time') {
        return {
          ...currentForm,
          status: value,
          paidInstallments: value === 'completed' ? 1 : 0,
          totalInstallments: 1
        };
      }

      return {
        ...currentForm,
        [field]: value
      };
    });
    setFormErrors((currentErrors) => ({
      ...currentErrors,
      [field === 'procurementDeductionTypes' ? 'deductionType' : field]: ''
    }));
  };

  const handleStoreChange = (store) => {
    const availableEmployees = employeeDirectory[store] || [];
    const selectedEmployeeStillValid = availableEmployees.some((employee) => employee.employeeId === form.employeeId);

    setForm((currentForm) => ({
      ...currentForm,
      store,
      employeeName: selectedEmployeeStillValid ? currentForm.employeeName : '',
      employeeId: selectedEmployeeStillValid ? currentForm.employeeId : ''
    }));
    setFormErrors((currentErrors) => ({
      ...currentErrors,
      store: '',
      employeeId: ''
    }));
  };

  const handleEmployeeChange = (employeeId) => {
    const selectedEmployee = (employeeDirectory[form.store] || []).find((employee) => employee.employeeId === employeeId);

    setForm((currentForm) => ({
      ...currentForm,
      employeeId: selectedEmployee?.employeeId || '',
      employeeName: selectedEmployee?.employeeName || ''
    }));
    setFormErrors((currentErrors) => ({
      ...currentErrors,
      employeeId: ''
    }));
  };

  const validateForm = () => {
    const nextErrors = {};
    const isOneTime = form.frequency === 'One time';

    if (!form.employeeId.trim()) nextErrors.employeeId = 'Employee selection is required.';
    if (!form.store) nextErrors.store = 'Store is required.';
    if (!form.deductionType) nextErrors.deductionType = `${activeDepartment.typeLabel} is required.`;
    if (!isOneTime && (!form.amount || Number(form.amount) <= 0)) nextErrors.amount = 'Amount must be greater than zero.';
    if (!form.totalAmount || Number(form.totalAmount) <= 0) nextErrors.totalAmount = 'Total amount must be greater than zero.';
    if (!isOneTime && Number(form.paidInstallments) < 0) nextErrors.paidInstallments = 'Progress cannot be negative.';
    if (!form.firstDeductionDate) nextErrors.firstDeductionDate = isOneTime ? 'Deduction date is required.' : 'First deduction date is required.';
    if (!isOneTime && derivedTotalInstallments <= 0) {
      nextErrors.amount = nextErrors.amount || 'Amount per run must be greater than zero.';
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async () => {
    if (isSubmitting || !validateForm()) {
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = buildPayloadFromForm();
      const response = form.id
        ? await updateOtherDeduction(form.id, payload)
        : await createOtherDeduction(payload);

      if (response?.data) {
        syncRecord(response.data);
      }

      setDialogOpen(false);
      enqueueSnackbar(
        form.id ? 'Employee deduction updated successfully.' : 'Employee deduction saved successfully.',
        { variant: 'success' }
      );
    } catch (error) {
      console.error('Failed to save deduction:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to save deduction.',
        { variant: 'error' }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleArchive = async () => {
    if (!form.id) {
      return;
    }

    try {
      const response = await archiveOtherDeduction(form.id);
      if (response?.data) {
        syncRecord(response.data);
      }

      setDialogOpen(false);
      enqueueSnackbar('Deduction archived successfully.', { variant: 'info' });
    } catch (error) {
      console.error('Failed to archive deduction:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to archive deduction.',
        { variant: 'error' }
      );
    }
  };

  const handleUnarchive = async (deductionRow) => {
    if (!deductionRow?.id) {
      return;
    }

    try {
      const response = await unarchiveOtherDeduction(deductionRow.id);
      if (response?.data) {
        syncRecord(response.data);
      }

      setProgressDialogRow(null);
      enqueueSnackbar('Deduction restored successfully.', { variant: 'success' });
    } catch (error) {
      console.error('Failed to restore deduction:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to restore deduction.',
        { variant: 'error' }
      );
    }
  };

  const handleDelete = async (deductionRow = form) => {
    if (!deductionRow?.id) {
      return;
    }

    const confirmed = window.confirm(`Delete ${deductionRow.deductionType || 'this deduction'} for ${deductionRow.employeeName || 'this employee'}?`);
    if (!confirmed) {
      return;
    }

    try {
      await deleteOtherDeduction(deductionRow.id);
      setRecords((currentRecords) => {
        const nextRecords = currentRecords.filter((record) => record.id !== deductionRow.id);
        recordsCacheRef.current[department] = {
          records: nextRecords,
          typesByDepartment: buildDepartmentTypeMapFromRecords(nextRecords),
          customTypesByDepartment: {
            [department]: customDeductionTypesByDepartment[department] || []
          },
        };
        return nextRecords;
      });
      setDialogOpen(false);
      setProgressDialogRow((currentRow) => (currentRow?.id === deductionRow.id ? null : currentRow));
      enqueueSnackbar('Deduction deleted successfully.', { variant: 'success' });
    } catch (error) {
      console.error('Failed to delete deduction:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to delete deduction.',
        { variant: 'error' }
      );
    }
  };

  const handleCreateDeductionType = async (rawType) => {
    const nextType = String(rawType || '').trim();
    if (!nextType) {
      return;
    }

    if (!isValidCustomDeductionTypeName(nextType)) {
      enqueueSnackbar('Deduction item names can only contain letters, numbers, and spaces.', {
        variant: 'error'
      });
      return;
    }

    const existingType = activeDepartmentTypes.find(
      (type) => type.toLowerCase() === nextType.toLowerCase()
    );
    const resolvedType = existingType || nextType;

    if (existingType) {
      enqueueSnackbar(`${resolvedType} already exists in ${activeDepartment.label} deduction items.`, {
        variant: 'info'
      });
      setForm((currentForm) => (
        department === 'procurement'
          ? buildProcurementSelectionFormState(currentForm, [
              ...(currentForm.procurementDeductionTypes || []),
              resolvedType
            ])
          : {
              ...currentForm,
              deductionType: resolvedType
            }
      ));
      setFormErrors((currentErrors) => ({
        ...currentErrors,
        deductionType: ''
      }));
      return;
    }

    try {
      const response = await createOtherDeductionType({
        department,
        name: nextType
      });
      const savedType = response?.data
        ? {
            id: response.data.id || response.data._id,
            name: response.data.name,
            price: Number(response.data.price || 0)
          }
        : null;

      if (savedType) {
        syncCustomTypesForDepartment(department, [savedType]);
      }

      setDepartmentTypeOptions((currentOptions) => ({
        ...currentOptions,
        [department]: normalizeDepartmentTypeOptions(department, [
          ...(currentOptions[department] || activeDepartment.types || []),
          nextType
        ])
      }));
      setForm((currentForm) => (
        department === 'procurement'
          ? buildProcurementSelectionFormState(currentForm, [
              ...(currentForm.procurementDeductionTypes || []),
              resolvedType
            ])
          : {
              ...currentForm,
              deductionType: resolvedType
            }
      ));
      setFormErrors((currentErrors) => ({
        ...currentErrors,
        deductionType: ''
      }));
      enqueueSnackbar(`${resolvedType} saved for ${activeDepartment.label}.`, {
        variant: 'success'
      });
    } catch (error) {
      console.error('Failed to save custom deduction type:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to save custom deduction type.',
        { variant: 'error' }
      );
    }
  };

  const handleOpenProcurementPricingDialog = () => {
    if (department !== 'procurement') {
      return;
    }

    setProcurementPricingDialogOpen(true);
  };

  const handleCloseProcurementPricingDialog = () => {
    if (procurementTypeSaving) {
      return;
    }

    setProcurementPricingDialogOpen(false);
  };

  const handleProcurementTypeSearchChange = (value) => {
    setProcurementTypeSearchInput(value);
    setProcurementTypePage(1);
  };

  const handleProcurementTypePageChange = (nextPage) => {
    setProcurementTypePage(nextPage);
  };

  const handleProcurementTypeRowsPerPageChange = (nextRowsPerPage) => {
    setProcurementTypeRowsPerPage(nextRowsPerPage);
    setProcurementTypePage(1);
  };

  const handleProcurementTypePriceChange = (typeRow, nextValue) => {
    setProcurementTypeDrafts((currentDrafts) => ({
      ...currentDrafts,
      [typeRow.id]: nextValue
    }));
  };

  const getProcurementTypeDraftValue = (typeRow) => (
    Object.prototype.hasOwnProperty.call(procurementTypeDrafts, typeRow.id)
      ? procurementTypeDrafts[typeRow.id]
      : Number(typeRow.price || 0).toFixed(2)
  );

  const hasPendingProcurementTypeChange = (typeRow) => {
    if (!Object.prototype.hasOwnProperty.call(procurementTypeDrafts, typeRow.id)) {
      return false;
    }

    const numericPrice = Number(procurementTypeDrafts[typeRow.id]);
    if (!Number.isFinite(numericPrice) || numericPrice < 0) {
      return false;
    }

    return Number(numericPrice.toFixed(2)) !== Number(Number(typeRow.price || 0).toFixed(2));
  };

  const handleSaveProcurementTypePrices = async () => {
    const invalidEntries = Object.entries(procurementTypeDrafts).filter(([_typeId, priceValue]) => {
      const numericPrice = Number(priceValue);
      return !Number.isFinite(numericPrice) || numericPrice < 0;
    });

    if (invalidEntries.length > 0) {
      enqueueSnackbar('Every procurement price must be 0 or greater before saving.', {
        variant: 'error'
      });
      return;
    }

    if (procurementTypePendingEntries.length === 0) {
      enqueueSnackbar('There are no procurement price changes to save.', {
        variant: 'info'
      });
      return;
    }

    try {
      setProcurementTypeSaving(true);
      const response = await bulkUpdateOtherDeductionTypes({
        updates: procurementTypePendingEntries.map(([typeId, priceValue]) => ({
          typeId,
          price: Number(priceValue)
        }))
      });
      const savedTypes = Array.isArray(response?.data)
        ? response.data.map((item) => ({
            id: item.id || item._id,
            name: item.name,
            price: Number(item.price || 0)
          }))
        : [];

      syncCustomTypesForDepartment('procurement', savedTypes);
      setProcurementTypeRows((currentRows) => currentRows.map((row) => {
        const updatedType = savedTypes.find((item) => item.id === row.id);
        return updatedType ? { ...row, price: updatedType.price } : row;
      }));
      setProcurementTypeDrafts((currentDrafts) => {
        const nextDrafts = { ...currentDrafts };
        savedTypes.forEach((item) => {
          delete nextDrafts[item.id];
        });
        return nextDrafts;
      });
      enqueueSnackbar(`Saved ${savedTypes.length} procurement deduction item price${savedTypes.length === 1 ? '' : 's'}.`, {
        variant: 'success'
      });
      setProcurementPricingDialogOpen(false);
    } catch (error) {
      console.error('Failed to bulk save procurement deduction item prices:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to save procurement deduction item prices.',
        { variant: 'error' }
      );
    } finally {
      setProcurementTypeSaving(false);
    }
  };

  const handleDeleteDeductionType = async (typeOption) => {
    if (typeOption?.isDetached) {
      return;
    }

    const typeToDelete = typeOption.value || typeOption.label || '';
    const normalizedTypeToDelete = String(typeToDelete || '').trim().toLowerCase();
    if (!normalizedTypeToDelete) {
      return;
    }

    const confirmed = window.confirm(`Delete "${typeToDelete}" from ${activeDepartment.label} loan types?`);
    if (!confirmed) {
      return;
    }

    try {
      let resolvedTypeId = typeOption?.typeId;

      if (!resolvedTypeId) {
        const refreshResponse = await getOtherDeductions(
          { department, include_archived: true }
        );
        const refreshedCustomTypes = normalizeCustomTypesMeta(
          refreshResponse?.meta?.custom_types_by_department || {}
        );

        setCustomDeductionTypesByDepartment((currentState) => ({
          ...currentState,
          ...refreshedCustomTypes
        }));
        setDepartmentTypeOptions((currentOptions) =>
          mergeDepartmentTypeMaps(currentOptions, refreshResponse?.meta?.types_by_department || {})
        );

        recordsCacheRef.current[department] = {
          ...(recordsCacheRef.current[department] || {}),
          records: Array.isArray(refreshResponse?.data)
            ? refreshResponse.data.map(normalizeApiDeductionRecord)
            : (recordsCacheRef.current[department]?.records || records),
          typesByDepartment: refreshResponse?.meta?.types_by_department || {},
          customTypesByDepartment: refreshedCustomTypes
        };

        resolvedTypeId = (refreshedCustomTypes[department] || []).find(
          (item) => String(item.name || '').trim().toLowerCase() === normalizedTypeToDelete
        )?.id;
      }

      if (!resolvedTypeId) {
        const createResponse = await createOtherDeductionType({
          department,
          name: typeToDelete
        });
        resolvedTypeId = createResponse?.data?.id || createResponse?.data?._id || '';
      }

      if (!resolvedTypeId) {
        enqueueSnackbar(`Could not resolve the saved record for "${typeToDelete}". Refresh and try again.`, {
          variant: 'error'
        });
        return;
      }

      await deleteOtherDeductionType(resolvedTypeId);

      setCustomDeductionTypesByDepartment((currentState) => {
        const nextCustomTypes = (currentState[department] || []).filter(
          (item) => String(item.name || '').trim().toLowerCase() !== normalizedTypeToDelete
        );

        recordsCacheRef.current[department] = {
          ...(recordsCacheRef.current[department] || {}),
          records: recordsCacheRef.current[department]?.records || records,
          typesByDepartment: {
            [department]: (departmentTypeOptions[department] || activeDepartment.types || []).filter(
              (type) => type.toLowerCase() !== normalizedTypeToDelete
            )
          },
          customTypesByDepartment: {
            [department]: nextCustomTypes
          }
        };

        return {
          ...currentState,
          [department]: nextCustomTypes
        };
      });

      setDepartmentTypeOptions((currentOptions) => ({
        ...currentOptions,
        [department]: (currentOptions[department] || activeDepartment.types || []).filter(
          (type) => type.toLowerCase() !== normalizedTypeToDelete
        )
      }));
      setProcurementTypeRows((currentRows) => currentRows.filter((item) => item.id !== resolvedTypeId));
      setProcurementTypeDrafts((currentDrafts) => {
        if (!Object.prototype.hasOwnProperty.call(currentDrafts, resolvedTypeId)) {
          return currentDrafts;
        }

        const nextDrafts = { ...currentDrafts };
        delete nextDrafts[resolvedTypeId];
        return nextDrafts;
      });

      enqueueSnackbar(`${typeToDelete} removed from ${activeDepartment.label} custom types.`, {
        variant: 'info'
      });
    } catch (error) {
      console.error('Failed to delete custom deduction type:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Failed to delete custom deduction type.',
        { variant: 'error' }
      );
    }
  };

  const handleOpenStore = (storeName) => {
    navigate(`/deductions/${department}/store?name=${encodeURIComponent(storeName)}`);
  };

  const handleOpenProgressDialog = (employeeRow) => {
    if (!employeeRow?.hasDeduction) {
      return;
    }

    setProgressDialogRow(employeeRow);
  };

  const handleExportEmployeeDeductions = (employeeRow) => {
    if (!employeeRow?.hasDeduction) {
      return;
    }

    const employeeDeductionRows = selectedStoreRecords
      .filter((record) => (
        record.employeeId === employeeRow.employeeId &&
        record.store === employeeRow.store
      ))
      .sort((left, right) => {
        const leftFinalized = FINALIZED_DEDUCTION_STATUSES.includes(left.status);
        const rightFinalized = FINALIZED_DEDUCTION_STATUSES.includes(right.status);

        if (leftFinalized !== rightFinalized) {
          return leftFinalized ? 1 : -1;
        }

        return String(left.firstDeductionDate || '').localeCompare(String(right.firstDeductionDate || ''));
      });

    exportEmployeeDeductionsPdf({
      employeeRow,
      deductionRows: employeeDeductionRows,
      activeDepartment
    });
  };

  const handleCloseProgressDialog = () => {
    setProgressDialogRow(null);
  };

  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setForm({
      ...createEmptyForm(),
      deductionType: department === 'procurement' ? '' : activeDepartmentTypes[0] || '',
      procurementDeductionTypes: [],
      procurementOriginalTotalAmount: '',
      procurementTotalOverridden: false
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCreateAdditionalDeduction = (employeeRow) => {
    setDialogMode('create');
    setForm({
      ...createEmptyForm(),
      store: employeeRow.store || selectedStoreName || '',
      employeeName: employeeRow.employeeName || '',
      employeeId: employeeRow.employeeId || '',
      deductionType: department === 'procurement' ? '' : activeDepartmentTypes[0] || '',
      procurementDeductionTypes: [],
      procurementOriginalTotalAmount: '',
      procurementTotalOverridden: false
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleOpenEmployeeDialog = (employeeRow) => {
    const procurementSelections = department === 'procurement'
      ? normalizeProcurementTypeSelection(
          employeeRow.hasDeduction
            ? (
                Array.isArray(employeeRow.deductionTypeItems) && employeeRow.deductionTypeItems.length > 0
                  ? employeeRow.deductionTypeItems
                  : splitProcurementDeductionTitle(employeeRow.deductionType)
              )
            : []
        )
      : [];
    const procurementOriginalTotalAmount = department === 'procurement' && procurementSelections.length > 0
      ? formatFormCurrencyValue(procurementSelections.reduce((sum, typeName) => (
          sum + Number(procurementTypePriceByName.get(typeName.toLowerCase()) || 0)
        ), 0))
      : '';
    setDialogMode(employeeRow.hasDeduction ? 'edit' : 'create');
    setForm({
      ...createEmptyForm(),
      ...employeeRow,
      amount: employeeRow.hasDeduction ? String(employeeRow.amount) : '',
      totalAmount: employeeRow.hasDeduction ? String(employeeRow.totalAmount) : '',
      paidInstallments: employeeRow.hasDeduction ? employeeRow.paidInstallments : 0,
      totalInstallments: employeeRow.hasDeduction ? employeeRow.totalInstallments : 1,
      deductionType: employeeRow.hasDeduction
        ? employeeRow.deductionType
        : department === 'procurement'
          ? ''
          : activeDepartmentTypes[0] || '',
      procurementDeductionTypes: procurementSelections,
      procurementOriginalTotalAmount,
      procurementTotalOverridden: department === 'procurement' && employeeRow.hasDeduction
        ? !areCurrencyValuesEqual(employeeRow.totalAmount, procurementOriginalTotalAmount)
        : false,
      firstDeductionDate: employeeRow.hasDeduction && employeeRow.firstDeductionDate ? employeeRow.firstDeductionDate : createEmptyForm().firstDeductionDate,
      lastDeductionDate: employeeRow.hasDeduction && employeeRow.lastDeductionDate ? employeeRow.lastDeductionDate : ''
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  if (storesLoading || recordsLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width={240} height={42} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={140} sx={{ borderRadius: 3, mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={420} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  const headerBreadcrumbs = isStoreDetailView
    ? [
        { label: 'Deductions', to: `/deductions/${department}` },
        { label: activeDepartment.label, to: `/deductions/${department}` },
        { label: selectedStoreName }
      ]
    : [{ label: 'Deductions' }, { label: activeDepartment.label }];

  return (
    <Fade in timeout={500}>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <DeductionsHeader
          title="Deductions Dashboard"
          breadcrumbs={headerBreadcrumbs}
          isMobile={isMobile}
          exportDisabled={isStoreDetailView ? employeeRows.length === 0 : storeRows.length === 0}
          onRefresh={handleRefresh}
          onExport={handleExport}
          primaryAction={
            !isStoreDetailView
              ? {
                  label: 'Add Deductions',
                  onClick: handleOpenCreateDialog,
                  color: activeDepartment.accent
                }
              : null
          }
          backAction={
            isStoreDetailView
              ? {
                  label: 'Back to Stores',
                  onClick: () => navigate(`/deductions/${department}`)
                }
              : null
          }
        />

        {!isStoreDetailView && (
          <>
            <DeductionsOverview
              activeDepartment={activeDepartment}
              isMobile={isMobile}
              heroTitle={`${activeDepartment.label} Deductions`}
              summaryCards={departmentSummaryCards}
            />
            <DeductionsStoreTable
              activeDepartment={activeDepartment}
              storeRows={storeRows}
              paginatedRows={paginatedStoreRows}
              totalPages={storeRowsPageCount}
              page={page}
              rowsPerPage={rowsPerPage}
              searchTerm={searchTerm}
              totalStores={allStoreRows.length}
              currencyFormatter={currencyFormatter}
              onSearchTermChange={setSearchTerm}
              onRowsPerPageChange={setRowsPerPage}
              onPageChange={setPage}
              onOpenStore={handleOpenStore}
            />
          </>
        )}

        {isStoreDetailView && (
          <>
            <DeductionsOverview
              activeDepartment={activeDepartment}
              isMobile={isMobile}
              heroTitle={selectedStoreName}
              highlightPrimary={{ label: 'Department', value: activeDepartment.label }}
              highlightSecondary={{
                label: 'Employees with deductions',
                value: String(selectedStoreRow?.employeesWithDeductions || 0)
              }}
              summaryCards={detailSummaryCards}
            />
            <DeductionsEmployeesTable
              activeDepartment={activeDepartment}
              employeeRows={employeeRows}
              paginatedRows={paginatedEmployeeRows}
              totalPages={employeeRowsPageCount}
              page={page}
              rowsPerPage={rowsPerPage}
              searchTerm={searchTerm}
              isMobile={isMobile}
              currencyFormatter={currencyFormatter}
              statusPalette={statusPalette}
              onSearchTermChange={setSearchTerm}
              onRowsPerPageChange={setRowsPerPage}
              onPageChange={setPage}
              onViewEmployee={handleOpenProgressDialog}
              onExportEmployeeDeductions={handleExportEmployeeDeductions}
              onAddEmployeeDeduction={handleCreateAdditionalDeduction}
              onEditEmployee={handleOpenEmployeeDialog}
            />
          </>
        )}

        <Suspense fallback={null}>
          <DeductionDialog
            open={dialogOpen}
            mode={dialogMode}
            activeDepartment={activeDepartment}
            storeOptions={storeOptions}
            employeeOptions={employeeOptions}
            deductionTypeOptions={deductionTypeOptions}
            employeeOptionsLoading={employeeOptionsLoading}
            employeeOptionsHelperText={employeeOptionsHelperText}
            form={form}
            derivedTotalInstallments={derivedTotalInstallments}
            derivedLastDeductionDate={derivedLastDeductionDate}
            totalAmountHelperText={totalAmountHelperText}
            formErrors={formErrors}
            isSubmitting={isSubmitting}
            onClose={handleCloseDialog}
            onSave={handleSave}
            onArchive={handleArchive}
            onDelete={handleDelete}
            onCreateDeductionType={handleCreateDeductionType}
            onDeleteDeductionType={handleDeleteDeductionType}
            onOpenProcurementPricing={handleOpenProcurementPricingDialog}
            onFormChange={handleFormChange}
            onStoreChange={handleStoreChange}
            onEmployeeChange={handleEmployeeChange}
            disableStoreSelection={isStoreDetailView}
            disableEmployeeSelection={isStoreDetailView}
          />

          <ProcurementTypePricingDialog
            open={procurementPricingDialogOpen}
            rows={procurementTypeRows}
            loading={procurementTypeLoading}
            saving={procurementTypeSaving}
            searchValue={procurementTypeSearchInput}
            page={procurementTypePage}
            rowsPerPage={procurementTypeRowsPerPage}
            totalCount={procurementTypeTotalCount}
            pendingEditCount={procurementTypePendingEntries.length}
            currencyFormatter={currencyFormatter}
            getDraftValue={getProcurementTypeDraftValue}
            hasPendingChange={hasPendingProcurementTypeChange}
            onClose={handleCloseProcurementPricingDialog}
            onSave={handleSaveProcurementTypePrices}
            onSearchChange={handleProcurementTypeSearchChange}
            onPageChange={handleProcurementTypePageChange}
            onRowsPerPageChange={handleProcurementTypeRowsPerPageChange}
            onPriceChange={handleProcurementTypePriceChange}
          />

          <DeductionProgressDialog
            open={Boolean(progressDialogRow)}
            row={progressDialogRow}
            activeDepartment={activeDepartment}
            currencyFormatter={currencyFormatter}
            statusPalette={statusPalette}
            onDelete={handleDelete}
            onUnarchive={handleUnarchive}
            onClose={handleCloseProgressDialog}
          />
        </Suspense>
      </Box>
    </Fade>
  );
};

export default Deductions;
